/**
 * Tasks Redux Slice
 *
 * Manages state for task queue operations including status tracking,
 * task history, and polling state in Redux store.
 */

import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import type {
  Task,
  TaskStatusResponse,
  TaskListResponse,
  TaskListQuery,
} from '@/types/tasks';
import {
  getTaskStatus,
  getTaskResult,
  cancelTask,
  listTasks,
} from '@/services/tasksApi';

// ============================================================================
// Tasks State Interface
// ============================================================================

interface TasksState {
  tasks: Record<string, Task>; // taskId -> task
  taskList: Task[];
  taskListTotal: number;
  activeTaskIds: string[]; // List of task IDs being polled
  loading: Record<string, boolean>; // taskId -> loading state
  error: string | null;
}

const initialTasksState: TasksState = {
  tasks: {},
  taskList: [],
  taskListTotal: 0,
  activeTaskIds: [],
  loading: {},
  error: null,
};

// ============================================================================
// Async Thunks
// ============================================================================

/**
 * Fetch task status by ID
 */
export const fetchTaskStatus = createAsyncThunk(
  'tasks/fetchStatus',
  async (taskId: string) => {
    const response = await getTaskStatus(taskId);
    return response;
  }
);

/**
 * Fetch task result by ID
 */
export const fetchTaskResult = createAsyncThunk(
  'tasks/fetchResult',
  async (taskId: string) => {
    const response = await getTaskResult(taskId);
    return response;
  }
);

/**
 * Cancel a task by ID
 */
export const cancelTaskThunk = createAsyncThunk(
  'tasks/cancel',
  async (taskId: string) => {
    await cancelTask(taskId);
    return taskId;
  }
);

/**
 * List tasks with optional filtering
 */
export const fetchTaskList = createAsyncThunk(
  'tasks/fetchList',
  async (query: TaskListQuery = {}) => {
    const response = await listTasks(query);
    return response;
  }
);

// ============================================================================
// Slice
// ============================================================================

const tasksSlice = createSlice({
  name: 'tasks',
  initialState: initialTasksState,
  reducers: {
    /**
     * Update task status (used by polling hook)
     * Handles case where task doesn't exist yet by creating it with proper defaults
     */
    updateTaskStatus: (state, action: PayloadAction<TaskStatusResponse>) => {
      const { task_id, ...taskData } = action.payload;
      
      // Get existing task or use undefined
      const existingTask = state.tasks[task_id];
      
      // Create or update task with proper default values
      state.tasks[task_id] = {
        ...existingTask,
        ...taskData,
        created_at: existingTask?.created_at || new Date().toISOString(),
        user_id: existingTask?.user_id || 0,
      } as Task;

      // Update task list if present
      const taskListIndex = state.taskList.findIndex(t => t.task_id === task_id);
      if (taskListIndex !== -1) {
        state.taskList[taskListIndex] = {
          ...state.taskList[taskListIndex],
          ...taskData,
        } as Task;
      }

      // Remove from active polling if completed
      const isCompleted = ['SUCCESS', 'FAILURE', 'CANCELLED'].includes(taskData.status);
      if (isCompleted) {
        state.activeTaskIds = state.activeTaskIds.filter(id => id !== task_id);
      }
    },

    /**
     * Add task to active polling list
     */
    addActiveTask: (state, action: PayloadAction<string>) => {
      if (!state.activeTaskIds.includes(action.payload)) {
        state.activeTaskIds.push(action.payload);
      }
    },

    /**
     * Remove task from active polling list
     */
    removeActiveTask: (state, action: PayloadAction<string>) => {
      state.activeTaskIds = state.activeTaskIds.filter(id => id !== action.payload);
    },

    /**
     * Clear task error
     */
    clearTasksError: (state) => {
      state.error = null;
    },

    /**
     * Clear completed tasks from state
     */
    clearCompletedTasks: (state) => {
      state.activeTaskIds = [];
    },
  },
  extraReducers: (builder) => {
    // Fetch Task Status
    builder
      .addCase(fetchTaskStatus.pending, (state, action) => {
        state.loading[action.meta.arg] = true;
        state.error = null;
      })
      .addCase(fetchTaskStatus.fulfilled, (state, action) => {
        state.loading[action.payload.task_id] = false;
        state.tasks[action.payload.task_id] = {
          ...state.tasks[action.payload.task_id],
          ...action.payload,
          created_at: state.tasks[action.payload.task_id]?.created_at || new Date().toISOString(),
          user_id: state.tasks[action.payload.task_id]?.user_id || 0,
        } as Task;

        // Update task list if present
        const taskListIndex = state.taskList.findIndex(t => t.task_id === action.payload.task_id);
        if (taskListIndex !== -1) {
          state.taskList[taskListIndex] = {
            ...state.taskList[taskListIndex],
            ...action.payload,
          } as Task;
        }

        // Remove from active polling if completed
        const isCompleted = ['SUCCESS', 'FAILURE', 'CANCELLED'].includes(action.payload.status);
        if (isCompleted) {
          state.activeTaskIds = state.activeTaskIds.filter(id => id !== action.payload.task_id);
        }
      })
      .addCase(fetchTaskStatus.rejected, (state, action) => {
        state.loading[action.meta.arg] = false;
        state.error = action.error.message || 'Failed to fetch task status';
      });

    // Fetch Task Result
    builder
      .addCase(fetchTaskResult.pending, (state, action) => {
        state.loading[action.meta.arg] = true;
        state.error = null;
      })
      .addCase(fetchTaskResult.fulfilled, (state, action) => {
        state.loading[action.payload.task_id] = false;
        if (state.tasks[action.payload.task_id]) {
          state.tasks[action.payload.task_id] = {
            ...state.tasks[action.payload.task_id],
            result: action.payload.result,
            error: action.payload.error,
            completed_at: action.payload.completed_at,
          };
        }
      })
      .addCase(fetchTaskResult.rejected, (state, action) => {
        state.loading[action.meta.arg] = false;
        state.error = action.error.message || 'Failed to fetch task result';
      });

    // Cancel Task
    builder
      .addCase(cancelTaskThunk.pending, (state, action) => {
        state.loading[action.meta.arg] = true;
        state.error = null;
      })
      .addCase(cancelTaskThunk.fulfilled, (state, action) => {
        state.loading[action.payload] = false;
        if (state.tasks[action.payload]) {
          state.tasks[action.payload].status = 'CANCELLED';
        }

        // Update task list if present
        const taskListIndex = state.taskList.findIndex(t => t.task_id === action.payload);
        if (taskListIndex !== -1) {
          state.taskList[taskListIndex].status = 'CANCELLED';
        }

        // Remove from active polling
        state.activeTaskIds = state.activeTaskIds.filter(id => id !== action.payload);
      })
      .addCase(cancelTaskThunk.rejected, (state, action) => {
        state.loading[action.meta.arg] = false;
        state.error = action.error.message || 'Failed to cancel task';
      });

    // Fetch Task List
    builder
      .addCase(fetchTaskList.pending, (state) => {
        state.error = null;
      })
      .addCase(fetchTaskList.fulfilled, (state, action) => {
        state.taskList = action.payload.tasks as Task[];
        state.taskListTotal = action.payload.total;

        // Update tasks cache
        action.payload.tasks.forEach(task => {
          state.tasks[task.task_id] = task as Task;
        });
      })
      .addCase(fetchTaskList.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to fetch task list';
      });
  },
});

export const {
  updateTaskStatus,
  addActiveTask,
  removeActiveTask,
  clearTasksError,
  clearCompletedTasks,
} = tasksSlice.actions;
export const tasksReducer = tasksSlice.reducer;

// ============================================================================
// Selectors
// ============================================================================

export const selectTask = (taskId: string) => (state: { tasks: TasksState }) =>
  state.tasks.tasks[taskId];

export const selectTaskLoading = (taskId: string) => (state: { tasks: TasksState }) =>
  state.tasks.loading[taskId] ?? false;

export const selectTaskList = (state: { tasks: TasksState }) =>
  state.tasks.taskList;

export const selectTaskListTotal = (state: { tasks: TasksState }) =>
  state.tasks.taskListTotal;

export const selectActiveTaskIds = (state: { tasks: TasksState }) =>
  state.tasks.activeTaskIds;

export const selectTasksError = (state: { tasks: TasksState }) =>
  state.tasks.error;
